import { api, httpClient } from './core';
import { grundHeader } from '../../components/ui/loeschen-mit-grund-dialog';

// --- Slot schema (mirrors backend app/schemas/cms_content.py) ----------------

export type CmsFieldType =
  | 'text'
  | 'textarea'
  | 'richtext'
  | 'image'
  | 'video'
  | 'select'
  | 'url'
  | 'icon'
  | 'toggle'
  | 'code'
  | 'list'
  | 'homepage_toggles'
  | 'standort_faecher'
  | 'standort_entity_toggle'
  | 'faq_select';

export interface CmsFieldSpec {
  key: string;
  label: string;
  type: CmsFieldType;
  help?: string | null;
  options?: string[] | null;
  item_fields?: CmsFieldSpec[] | null;
  /** homepage_toggles only: which entity collection ("standort" | "fach" | "partner"). */
  source?: string | null;
  /** Editor accordion section this field belongs to. */
  section?: string | null;
}

export interface CmsContentSchema {
  kind: string;
  label: string;
  fields: CmsFieldSpec[];
}

export type CmsContentValue = Record<string, unknown>;

export interface CmsContent {
  slug: string;
  kind: string;
  title: string;
  status: string;
  draft: CmsContentValue;
  published: CmsContentValue;
  published_at: string | null;
  /** Veröffentlicht, aber der Entwurf weicht ab (T-0212). */
  changes_pending?: boolean;
}

export interface CmsBlockSchema {
  type: string;
  label: string;
  fields: CmsFieldSpec[];
}

interface CmsContentSchemaResponse {
  schemas: Record<string, CmsContentSchema>;
  blocks?: Record<string, CmsBlockSchema>;
  reserved_paths?: string[];
}

export interface CmsCustomPage {
  id: number;
  slug: string;
  title: string;
  status: string;
  published_at: string | null;
  changes_pending?: boolean;
}

interface CmsPreviewTokenResponse {
  token: string;
  expires_in: number;
}

// --- Media (existing cms-media endpoints) ------------------------------------

export interface CmsMediaItem {
  id: number;
  tenant_id: string;
  original_filename: string;
  content_type: string;
  content_length: number;
  visibility: string;
  bucket_name: string;
  object_key: string;
  status: string;
  title: string | null;
  alt_text: string | null;
  public_url: string | null;
  folder_id: number | null;
  uploaded_at: string | null;
  upload_etag: string | null;
  media_kind: string;
  ai_disclosure: AiDisclosure;
  processing_status: string | null;
  hls_playlist_url: string | null;
  /** Vorschaubild: bei Video aus dem Transcode, bei Bildern die 320-px-Fassung. */
  thumbnail_url: string | null;
  /** Nur bei quadratischen Bildern: 64 und 256 px, als Favicon brauchbar. */
  icon_url: string | null;
  icon_large_url: string | null;
  duration_seconds: number | null;
}

/**
 * KI-Kennzeichnung nach Art. 50 Abs. 4 KI-VO (seit 02.08.2026).
 *
 * Kennzeichnungspflichtig sind Medien, die real wirken könnten — KI-„Fotos",
 * synthetische Gesichter, fotorealistische Renderings. Offensichtlich fiktive
 * Illustrationen und reine Retusche (Licht, Farbe) fallen nicht darunter.
 *
 * Die beiden gekennzeichneten Werte entsprechen den EU-Icons „fully
 * AI-generated" bzw. „partially AI-modified".
 */
export type AiDisclosure = 'none' | 'ai_generated' | 'ai_modified';

export const AI_DISCLOSURE_LABELS: Record<AiDisclosure, string> = {
  none: 'Keine KI-Kennzeichnung',
  ai_generated: 'KI-generiert',
  ai_modified: 'Mit KI bearbeitet',
};

export interface CmsMediaProcessing {
  id: number;
  media_kind: string;
  processing_status: string | null;
  hls_playlist_url: string | null;
  thumbnail_url: string | null;
  icon_url?: string | null;
  icon_large_url?: string | null;
  duration_seconds: number | null;
}

export interface CmsMediaFolder {
  id: number;
  name: string;
  media_count: number;
}

interface CmsMediaFolderListResponse {
  items: CmsMediaFolder[];
  unfiled_count: number;
  total_count: number;
}

interface CmsMediaListResponse {
  items: CmsMediaItem[];
  total: number;
  limit: number;
  offset: number;
}

interface CmsMediaCreateResponse {
  item: CmsMediaItem;
  upload_url: string;
  upload_method: string;
  upload_expires_in: number;
}

export const cmsApi = {
  getSchemas: (): Promise<Record<string, CmsContentSchema>> =>
    api.get<CmsContentSchemaResponse>('/api/cms/content-schema').then((r) => r.schemas),

  getBlockSchemas: (): Promise<Record<string, CmsBlockSchema>> =>
    api.get<CmsContentSchemaResponse>('/api/cms/content-schema').then((r) => r.blocks ?? {}),

  getReservedPaths: (): Promise<string[]> =>
    api.get<CmsContentSchemaResponse>('/api/cms/content-schema').then((r) => r.reserved_paths ?? []),

  getContent: (slug: string): Promise<CmsContent> =>
    api.get<CmsContent>(`/api/cms/content/${slug}`),

  saveDraft: (
    slug: string,
    payload: { title?: string; content: CmsContentValue },
  ): Promise<CmsContent> =>
    api.put<CmsContent>(`/api/cms/content/${slug}/draft`, payload),

  publish: (slug: string): Promise<CmsContent> =>
    api.post<CmsContent>(`/api/cms/content/${slug}/publish`, {}),

  getPreviewToken: (): Promise<CmsPreviewTokenResponse> =>
    api.post<CmsPreviewTokenResponse>('/api/cms/preview-token', {}),

  /** Zwei Minuten gültig; die Website tauscht es sofort gegen eine eigene Builder-Sitzung. */
  getBuilderUebergabe: (): Promise<CmsPreviewTokenResponse> =>
    api.post<CmsPreviewTokenResponse>('/api/cms/builder-uebergabe', {}),
};

/**
 * Der ThunderBuilder (visueller Editor) läuft in der Website, nicht hier: Er
 * braucht React 19 und rendert die Seite mit den Komponenten der Website.
 */
export const builderUrl = (token: string, seiteId?: number): string =>
  `${WEBSITE_BASE_URL}/api/builder-anmeldung?token=${encodeURIComponent(token)}${seiteId ? `&seite=${seiteId}` : ''}`;

/** Design-Editor der Website (Farben, Schriften, Ecken) — dieselbe Übergabe, anderes Ziel. */
export const designUrl = (token: string): string =>
  `${WEBSITE_BASE_URL}/api/builder-anmeldung?token=${encodeURIComponent(token)}&ziel=design`;

export const cmsMediaApi = {
  list: (params?: {
    q?: string;
    visibility?: string;
    content_type?: string;
    status?: string;
    /** undefined = all, 0 = unfiled (root), >0 = that folder. */
    folder_id?: number;
    limit?: number;
    offset?: number;
  }): Promise<CmsMediaListResponse> => {
    const sp = new URLSearchParams();
    if (params?.q) sp.set('q', params.q);
    if (params?.visibility) sp.set('visibility', params.visibility);
    if (params?.content_type) sp.set('content_type', params.content_type);
    if (params?.status) sp.set('status', params.status);
    if (params?.folder_id !== undefined) sp.set('folder_id', String(params.folder_id));
    sp.set('limit', String(params?.limit ?? 60));
    sp.set('offset', String(params?.offset ?? 0));
    return api.get<CmsMediaListResponse>(`/api/cms-media?${sp.toString()}`);
  },

  move: (id: number, folderId: number): Promise<CmsMediaItem> =>
    // folderId 0 moves the asset back to root.
    api.patch<CmsMediaItem>(`/api/cms-media/${id}`, { folder_id: folderId }),

  /** KI-Kennzeichnung setzen — die Website badged darüber jedes Vorkommen. */
  setAiDisclosure: (id: number, value: AiDisclosure): Promise<CmsMediaItem> =>
    api.patch<CmsMediaItem>(`/api/cms-media/${id}`, { ai_disclosure: value }),

  /**
   * Upload a file: create the media record (gets a presigned URL), PUT the bytes
   * straight to storage, and return the stored asset. The asset's `public_url`
   * is what content fields reference.
   */
  upload: async (
    file: File,
    opts?: {
      title?: string;
      altText?: string;
      visibility?: 'public' | 'private';
      folderId?: number;
      aiDisclosure?: AiDisclosure;
    },
  ): Promise<CmsMediaItem> => {
    const created = await api.post<CmsMediaCreateResponse>('/api/cms-media', {
      original_filename: file.name,
      content_type: file.type || 'application/octet-stream',
      content_length: file.size,
      visibility: opts?.visibility ?? 'public',
      title: opts?.title ?? null,
      alt_text: opts?.altText ?? null,
      folder_id: opts?.folderId ?? null,
      ai_disclosure: opts?.aiDisclosure ?? 'none',
    });

    const putRes = await fetch(created.upload_url, {
      method: created.upload_method || 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    });
    if (!putRes.ok) {
      throw new Error(`Upload fehlgeschlagen (HTTP ${putRes.status})`);
    }
    // Videos need a transcode step; kick it off right after the bytes land.
    if (created.item.media_kind === 'video') {
      try {
        const proc = await cmsMediaApi.processVideo(created.item.id);
        return { ...created.item, ...proc };
      } catch {
        // Leave the asset in "pending"; the library will surface a retry.
      }
    }
    // Bilder bekommen serverseitig eine Vorschau (320 px) und – wenn quadratisch –
    // Icon-Fassungen. Ohne sie zeigt die Mediathek das Original als Kachel; im
    // Bestand sind das im Schnitt 2,2 MB pro Bild.
    if (created.item.media_kind === 'image') {
      try {
        const derivate = await cmsMediaApi.generateDerivatives(created.item.id);
        return { ...created.item, ...derivate };
      } catch {
        // Nicht schlimm: die Vorschau faellt dann auf das Original zurueck.
      }
    }
    return created.item;
  },

  /** Vorschau- und Icon-Fassungen eines hochgeladenen Bildes erzeugen lassen. */
  generateDerivatives: (id: number): Promise<CmsMediaProcessing> =>
    api.post<CmsMediaProcessing>(`/api/cms-media/${id}/derivatives`, {}),

  /** Start HLS transcoding for an uploaded video. */
  processVideo: (id: number): Promise<CmsMediaProcessing> =>
    api.post<CmsMediaProcessing>(`/api/cms-media/${id}/process`, {}),

  /** Poll the current transcoding status of a video. */
  processingStatus: (id: number): Promise<CmsMediaProcessing> =>
    api.get<CmsMediaProcessing>(`/api/cms-media/${id}/processing`),

  remove: async (id: number): Promise<void> => {
    // DELETE returns 204 No Content, so use httpClient directly (api.delete
    // would treat the empty body as a missing-data error).
    const res = await httpClient<unknown>(`/api/cms-media/${id}`, {
      method: 'DELETE',
    } as RequestInit);
    if (res.error) throw new Error(res.error);
  },
};

// --- FamilyCard partners -----------------------------------------------------

export interface CmsPartner {
  id: number;
  name: string;
  logo_url: string | null;
  description: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  is_active: boolean;
  show_on_homepage: boolean;
  standort_ids: number[];
  sort_order: number;
  benefits: string[];
  disclaimer: string | null;
}

export type CmsPartnerInput = {
  name: string;
  logo_url?: string | null;
  description?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  is_active?: boolean;
  show_on_homepage?: boolean;
  standort_ids?: number[];
  sort_order?: number;
  benefits?: string[];
  disclaimer?: string | null;
};

export const cmsPageApi = {
  list: (): Promise<CmsCustomPage[]> =>
    api.get<{ items: CmsCustomPage[] }>('/api/cms/custom-pages').then((r) => r.items),

  create: (slug: string, title: string): Promise<CmsCustomPage> =>
    api.post<CmsCustomPage>('/api/cms/custom-pages', { slug, title }),

  remove: async (id: number, grund: string): Promise<void> => {
    const res = await httpClient<unknown>(`/api/cms/custom-pages/${id}`, { method: 'DELETE', ...grundHeader(grund) } as RequestInit);
    if (res.error) throw new Error(res.error);
  },
};

/** Landingpage unter /lp/… — gestaltet im ThunderBuilder, Fach und Standort füllen die Platzhalter. */
export interface CmsLandingpage {
  id: number;
  slug: string;
  pfad: string;
  title: string;
  status: string;
  published_at: string | null;
  fach_id: number | null;
  fach_name: string | null;
  standort_id: number | null;
  standort_name: string | null;
  kampagne: string | null;
  noindex: boolean;
  hat_dokument: boolean;
  /** Gewicht des Originals im A/B-Test. */
  gewicht: number;
  aktive_varianten: number;
}

/** A/B-Variante: eigene Seite /lp/<pfad>~<schlüssel>, gestaltet im Editor. */
export interface CmsLandingpageVariante {
  id: number;
  page_id: number;
  schluessel: string;
  name: string;
  pfad: string;
  gewicht: number;
  aktiv: boolean;
  status: string;
  hat_dokument: boolean;
  aufrufe: number;
  anfragen: number;
}

export interface CmsLandingpageVarianten {
  original: { schluessel: string; gewicht: number; aufrufe: number; anfragen: number };
  items: CmsLandingpageVariante[];
}

/** Variante im Zeitraum, verglichen mit dem Original (zweiseitiger z-Test im Backend). */
export interface CmsLandingpageAuswertungVariante {
  schluessel: string;
  /** null: Original oder gelöschte Variante. */
  varianten_id: number | null;
  name: string;
  aktiv: boolean;
  aufrufe: number;
  anfragen: number;
  rate: number;
  urteil: 'original' | 'besser' | 'schlechter' | 'offen' | 'zu_wenig_daten';
  /** Relativ zur Rate des Originals, 0.2 = +20 %. */
  veraenderung: number | null;
  p_wert: number | null;
  /** Aufrufe je Variante, ab denen der beobachtete Unterschied belastbar wäre. */
  noetige_aufrufe: number | null;
  aufrufe_reihe: number[];
  anfragen_reihe: number[];
}

export interface CmsLandingpageAuswertung {
  von: string;
  bis: string;
  tage: string[];
  schwelle: number;
  varianten: CmsLandingpageAuswertungVariante[];
}

export interface CmsLandingpageEingabe {
  slug: string;
  title: string;
  fach_id: number | null;
  standort_id: number | null;
  kampagne: string | null;
  noindex: boolean;
}

export type CmsLandingpageAenderung = Partial<Omit<CmsLandingpageEingabe, 'slug'>> & { online?: boolean; gewicht?: number };

export const cmsLandingpageApi = {
  list: (): Promise<CmsLandingpage[]> =>
    api.get<{ items: CmsLandingpage[] }>('/api/cms/landingpages').then((r) => r.items),

  create: (eingabe: CmsLandingpageEingabe): Promise<CmsLandingpage> =>
    api.post<CmsLandingpage>('/api/cms/landingpages', eingabe),

  update: (id: number, aenderung: CmsLandingpageAenderung): Promise<CmsLandingpage> =>
    api.patch<CmsLandingpage>(`/api/cms/landingpages/${id}`, aenderung),

  remove: async (id: number, grund: string): Promise<void> => {
    const res = await httpClient<unknown>(`/api/cms/landingpages/${id}`, { method: 'DELETE', ...grundHeader(grund) } as RequestInit);
    if (res.error) throw new Error(res.error);
  },

  varianten: (id: number): Promise<CmsLandingpageVarianten> =>
    api.get<CmsLandingpageVarianten>(`/api/cms/landingpages/${id}/varianten`),

  varianteAnlegen: (id: number, name: string): Promise<CmsLandingpageVariante> =>
    api.post<CmsLandingpageVariante>(`/api/cms/landingpages/${id}/varianten`, { name }),

  varianteAendern: (
    id: number,
    variante: number,
    aenderung: Partial<Pick<CmsLandingpageVariante, 'name' | 'gewicht' | 'aktiv'>>,
  ): Promise<CmsLandingpageVariante> =>
    api.patch<CmsLandingpageVariante>(`/api/cms/landingpages/${id}/varianten/${variante}`, aenderung),

  varianteLoeschen: async (id: number, variante: number, grund: string): Promise<void> => {
    const res = await httpClient<unknown>(`/api/cms/landingpages/${id}/varianten/${variante}`, {
      method: 'DELETE',
      ...grundHeader(grund),
    } as RequestInit);
    if (res.error) throw new Error(res.error);
  },

  /** Zeitraum in Tagen, oder null für alles seit dem ersten Aufruf. */
  auswertung: (id: number, tage: number | null): Promise<CmsLandingpageAuswertung> =>
    api.get<CmsLandingpageAuswertung>(`/api/cms/landingpages/${id}/auswertung?${tage === null ? 'alles=true' : `tage=${tage}`}`),

  /** Gewinner übernehmen: Variante wird das Original, alle Varianten gehen aus. */
  uebernehmen: (id: number, variante: number): Promise<{ version: number }> =>
    api.post<{ version: number }>(`/api/cms/landingpages/${id}/varianten/${variante}/uebernehmen`, {}),

  /** Auswahl für Fach und Standort (auch inaktive — eine Kampagne kann vorgreifen). */
  ziele: async (): Promise<{ faecher: { id: number; name: string }[]; standorte: { id: number; name: string }[] }> => {
    const liste = async (pfad: string) => {
      const rows = await api.get<Array<{ id: number; name: string }>>(`${pfad}?active_only=false`);
      return (Array.isArray(rows) ? rows : []).map((r) => ({ id: r.id, name: r.name })).sort((a, b) => a.name.localeCompare(b.name, 'de'));
    };
    const [faecher, standorte] = await Promise.all([liste('/api/faecher'), liste('/api/standorte')]);
    return { faecher, standorte };
  },
};

export const cmsPartnerApi = {
  list: (): Promise<CmsPartner[]> =>
    api.get<{ items: CmsPartner[] }>('/api/cms-partners').then((r) => r.items),

  create: (data: CmsPartnerInput): Promise<CmsPartner> =>
    api.post<CmsPartner>('/api/cms-partners', data),

  update: (id: number, data: Partial<CmsPartnerInput>): Promise<CmsPartner> =>
    api.patch<CmsPartner>(`/api/cms-partners/${id}`, data),

  remove: async (id: number, grund: string): Promise<void> => {
    const res = await httpClient<unknown>(`/api/cms-partners/${id}`, { method: 'DELETE', ...grundHeader(grund) } as RequestInit);
    if (res.error) throw new Error(res.error);
  },
};

// --- Team members (central, assignable to standorte) -------------------------

export interface CmsTeamMember {
  id: number;
  name: string;
  aufgaben: string | null;
  beschreibung: string | null;
  portrait_url: string | null;
  portrait_alt: string | null;
  is_active: boolean;
  standort_ids: number[];
  fach_ids: number[];
  sort_order: number;
}

export type CmsTeamMemberInput = {
  name: string;
  aufgaben?: string | null;
  beschreibung?: string | null;
  portrait_url?: string | null;
  portrait_alt?: string | null;
  is_active?: boolean;
  standort_ids?: number[];
  fach_ids?: number[];
  sort_order?: number;
};

export const cmsTeamApi = {
  list: (): Promise<CmsTeamMember[]> =>
    api.get<{ items: CmsTeamMember[] }>('/api/cms-team-members').then((r) => r.items),

  create: (data: CmsTeamMemberInput): Promise<CmsTeamMember> =>
    api.post<CmsTeamMember>('/api/cms-team-members', data),

  update: (id: number, data: Partial<CmsTeamMemberInput>): Promise<CmsTeamMember> =>
    api.patch<CmsTeamMember>(`/api/cms-team-members/${id}`, data),

  remove: async (id: number, grund: string): Promise<void> => {
    const res = await httpClient<unknown>(`/api/cms-team-members/${id}`, { method: 'DELETE', ...grundHeader(grund) } as RequestInit);
    if (res.error) throw new Error(res.error);
  },
};

// --- Teachers as coaches (sourced from Personen) -----------------------------

export interface CmsTeacher {
  id: number;
  name: string;
  standort_ids: number[];
  fach_ids: number[];
  portrait_url: string | null;
  portrait_alt: string | null;
  public_bio: string | null;
}

export const cmsTeacherApi = {
  list: (): Promise<CmsTeacher[]> =>
    api.get<CmsTeacher[]>('/api/cms-teachers').then((r) => (Array.isArray(r) ? r : [])),
};

export interface CmsHomepageEntity {
  id: number;
  name: string;
  show_on_homepage: boolean;
}

/** Drives the homepage_toggles editor widget: list entities + flip their homepage flag. */
export const cmsHomepageApi = {
  list: async (source: string): Promise<CmsHomepageEntity[]> => {
    if (source === 'partner') {
      const partners = await cmsPartnerApi.list();
      return partners.map((p) => ({ id: p.id, name: p.name, show_on_homepage: p.show_on_homepage }));
    }
    const path = source === 'fach' ? '/api/faecher' : '/api/standorte';
    // The api core unwraps `{items}` list responses to a bare array for these
    // endpoints (see normalizeSuccessfulData), so read the array directly.
    const rows = await api.get<Array<{ id: number; name: string; show_on_homepage?: boolean }>>(
      `${path}?active_only=false`,
    );
    return (Array.isArray(rows) ? rows : []).map((i) => ({
      id: i.id,
      name: i.name,
      show_on_homepage: i.show_on_homepage !== false,
    }));
  },

  setVisible: async (source: string, id: number, value: boolean): Promise<void> => {
    if (source === 'partner') {
      await cmsPartnerApi.update(id, { show_on_homepage: value });
      return;
    }
    const path = source === 'fach' ? '/api/faecher' : '/api/standorte';
    await api.patch(`${path}/${id}`, { show_on_homepage: value });
  },
};

export const cmsFolderApi = {
  list: (): Promise<CmsMediaFolderListResponse> =>
    api.get<CmsMediaFolderListResponse>('/api/cms-media-folders'),

  create: (name: string): Promise<CmsMediaFolder> =>
    api.post<CmsMediaFolder>('/api/cms-media-folders', { name }),

  rename: (id: number, name: string): Promise<CmsMediaFolder> =>
    api.patch<CmsMediaFolder>(`/api/cms-media-folders/${id}`, { name }),

  remove: async (id: number): Promise<void> => {
    const res = await httpClient<unknown>(`/api/cms-media-folders/${id}`, {
      method: 'DELETE',
    } as RequestInit);
    if (res.error) throw new Error(res.error);
  },
};

/** Base URL of the public website, used to open preview links. */
export const WEBSITE_BASE_URL =
  (import.meta as ImportMeta & { env?: { VITE_WEBSITE_BASE?: string } }).env?.VITE_WEBSITE_BASE ??
  'https://fitinmusic.de';

/**
 * Map a CMS content slug to the public website path it controls.
 *
 * Slugs ohne eigene Seite (navbar, footer, cookie) zeigen auf die Startseite,
 * weil ihr Inhalt dort global gerendert wird.
 */
const FIXED_SLUG_PATHS: Record<string, string> = {
  home: '/',
  navbar: '/',
  footer: '/',
  cookie: '/',
  'ueber-uns': '/ueber-uns',
  faecher: '/faecher',
  standorte: '/standorte',
  faq: '/faq',
  jobs: '/jobs',
  familycard: '/familycard',
  rechtstexte: '/impressum',
};

export const websitePathForSlug = (slug: string): string => {
  const clean = slug.trim().replace(/^\/+|\/+$/g, '');
  const fixed = FIXED_SLUG_PATHS[clean];
  if (fixed) return fixed;
  if (clean.startsWith('standort/')) return `/standort/${clean.slice('standort/'.length)}`;
  if (clean.startsWith('fach/')) return `/fach/${clean.slice('fach/'.length)}`;
  if (clean.startsWith('page/')) return `/${clean.slice('page/'.length)}`;
  return '/';
};

/** Build a preview URL for a slug given a freshly issued preview token. */
export const buildPreviewUrl = (slug: string, token: string): string => {
  const path = websitePathForSlug(slug);
  const sep = path.includes('?') ? '&' : '?';
  return `${WEBSITE_BASE_URL}${path}${sep}preview=${encodeURIComponent(token)}`;
};
